import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useRef, // Import useRef
} from "react";
import { useAsyncError, useNavigate } from "react-router-dom";
import {
  chat,
  callFunction,
  createTask,
  fetchTasks,
  fetchTaskConversations,
  addConversationToTask,
  getToolsForSubtask,
  getUserInfo,
  deleteTask,
  streamChat,
  initializeStreamChat,
  createBotSteps,
  createTaskBot,
  fetchBots,
  checkTriggerPhrase,
  fetchBotSteps,
  fetchStepChats,
  listFiles,
  listFilesDataSource,
  getTaskDataSources,
  updateTaskDataSources,
  fetchBotsHistory,
  stopStreamChat,
  completeStreamChat
} from "../services/api";
import { getWorkflows } from "../services/workflowApi";

import { functionsNeedingApproval, functionsNotNeedingApproval, functionResponsesRequiringApproval } from "../utils/constants"

import TextFormat from "../components/TextFormat";
import useLocalStorage from "use-local-storage";
import useFileStatusPolling from "../hooks/useFileStatusPolling";
import toast from "react-hot-toast";
import { useGetPointerPosition } from "reactflow";
import { extractAndSetToolResponsesForReferencing, prepareFunctionArguments } from "../utils/helperFunctionsToolReferencing";
import { set } from "date-fns";

/**
 * Determines if a tool call requires user approval based on its name.
 * Approval is needed for any tool that changes the agent control flow.
 * @param {string} toolName - The name of the function to be called (e.g., "handoff_to_Documentation").
 * @returns {boolean} - True if the tool requires approval, false otherwise.
 */
const isAgentControlTool = (toolName) => {
  if (!toolName) {
    return false;
  }
  return toolName.startsWith('handoff_to_') || toolName.startsWith('return_to_');
};

export const DataContext = createContext({});

export const DataState = () => {
  const context = useContext(DataContext);
  if (!context) {
    // Return empty object instead of undefined to prevent destructuring errors
    // while the provider is still mounting or if accessed outside.
    return {};
  }
  return context;
};


const DataProvider = ({ children }) => {
  const defaultDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const [theme, setTheme] = useLocalStorage(
    "theme",
    defaultDark ? "dark" : "light"
  );

  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [input, setInput] = useState("");
  const [isAutomatic, setIsAutomatic] = useState(true);
  const [taskInput, setTaskInput] = useState("");
  // const [currentChat, setCurrentChat] = useState({ role: "user", content: "" });
  const [currentToolResults, setCurrentToolResults] = useState([]);
  const [creatingTask, setCreatingTask] = useState(true);
  const [error, setError] = useState(null);
  const [tools, setTools] = useState([]);

  // New Project Flow
  const [userGoogleId, setUserGoogleId] = useState(localStorage.getItem("projectToken") || ""); // ProjectIDToken
  const [userIdToken, setUserIdToken] = useState(localStorage.getItem("userToken") || ""); // UserIDToken
  const [justChangedLocalStorageData, setJustChangedLocalStorageData] = useState(false);


  const [subtasks, setSubtasks] = useState([]);
  const [showTools, setShowTools] = useState(false);
  const [hasTaskResults, setHasTaskResults] = useState(false); // New state
  const [isBusy, setIsBusy] = useState(false); // New state
  const [steps, setSteps] = useState("");

  const messageContainerRef = useRef(null);
  const [justSavedFiles, setJustSavedFiles] = useState(true);
  const [newTaskSubmitted, setNewTaskSubmitted] = useState(false);

  const [createTaskLoading, setCreateTaskLoading] = useState(false);
  const [sendTaskLoading, setSendTaskLoading] = useState(false);
  const [sendSubTaskLoading, setSubSendTaskLoading] = useState(false);
  // Boolean state for checking if the currently the model is streaming
  const [isStreaming, setIsStreaming] = useState(false);
  const completionEventSourceRef = useRef(null);

  const [streamingResponse, setStreamingResponse] = useState({
    role: "assistant",
    content: [],
    tool_calls: [],
  });

  const [isCreatingSubTask, setIsCreatingSubTask] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  const [bots, setBots] = useState([]);
  const [botsHistory, setBotsHistory] = useState([]);
  const [workflows, setWorkflows] = useState([]); // Graph Workflows State
  const [currentBotSessions, setCurrentBotSessions] = useState([]); // need to check use
  const [currentBotSession, setCurrentBotSession] = useState(null);
  const [currentBotId, setCurrentBotId] = useState(null);
  const [botCreationId, setBotCreationId] = useState(null);
  const [currentBot, setCurrentBot] = useState(null);
  const [clarificationQuestions, setClarificationQuestions] = useState([]);
  const [submitClarificationQuestions, setSubmitClarificationQuestions] =
    useState(false);
  const [needBotFetch, setNeedBotFetch] = useState(true);

  // For managing botstepsmodal
  const [isBotStepsModalOpen, setBotStepsModalOpen] = useState(false); // New state for BotStepsModal
  const [selectedBotId, setSelectedBotId] = useState(null);

  // Bot steps shown in UI
  const [botTaskId, setBotTaskId] = useState(null);
  const [showBotSteps, setShowBotSteps] = useState(false);
  const [justStartedBot, setJustStartedBot] = useState(false);
  const [newTriggerName, setNewTriggerName] = useState("");
  const [currentStepId, setCurrentStepId] = useState(null);
  const [botStepInputToSubmit, setBotStepInputToSubmit] = useState({});
  const [useAutomaticStepSwitching, setUseAutomaticStepSwitching] =
    useState(false);
  const [moveToNextStep, setMoveToNextStep] = useState(false);

  // Adding bot chats into normal chats
  const [botSessionJustCreatedId, setBotSessionJustCreatedId] = useState(null);
  const [currentTaskSessions, setCurrentTaskSessions] = useState({});
  const [currentBotSessionChats, setCurrentBotSessionChats] = useState({});
  // const [currentBotSteps, setCurrentBotSteps] = useState([]);
  const [trackExecution, setTrackExecution] = useState(null);

  const [allUserDataSourceFiles, setAllUserDataSourceFiles] = useState([]);
  const [currentTaskDataSourceFiles, setCurrentTaskDataSourceFiles] = useState(
    []
  );
  const [toolCallApprovalList, setToolCallApprovalList] = useState([]); // Holds all tool calls with approval info

  // Images that are currently set to be sent with next prompt
  const [currentTaskImages, setCurrentTaskImages] = useState([]);

  // Tool Call Status
  const [currentToolCallStatus, setCurrentToolCallStatus] = useState([]);
  const [currentToolCallStatusMap, setCurrentToolCallStatusMap] = useState(new Map());
  // In your DataProvider file...
  const [scrollToToolId, setScrollToToolId] = useState(null);
  const [currentReasoningCalls, setCurrentReasoningCalls] = useState([]);
  const [currentReasoningToolStreamingData, setCurrentReasoningToolStreamingData] = useState(null);


  // Right Sidebar Width 
  const [rightSidebarWidth, setRightSidebarWidth] = useState(256);

  // Reason Options
  const [selectedReason, setSelectedReason] = useState(null);
  const [isWebSearch, setIsWebSearch] = useState(false);
  const [isReasoning, setIsReasoning] = useState(false);
  const [isCanvas, setIsCanvas] = useState(false);
  const [isAgentMode, setIsAgentMode] = useState(false);
  const [agentSequence, setAgentSequence] = useState(["Orchestrator"]);
  const [agentTraversalPaths, setAgentTraversalPaths] = useState([]);
  const [taskMCPConnections, setTaskMCPConnections] = useState([]);
  const [taskOqlousTools, setTaskOqlousTools] = useState([]);
  const [taskManualToolSelection,
    setTaskManualToolSelection] = useState(false);

  const [currentTaskModel, setCurrentTaskModel] = useState(null);

  // SideBar Toggle
  const [toggleSidebar, setToggleSidebar] = useState(true);

  const [currentToolResponseForReference, setCurrentToolResponseForReference] = useState({});

  // --- NEW: Add state for the task plan ---
  const [currentTaskPlan, setCurrentTaskPlan] = useState([]);

  // Chat Status Message:
  const [statusMessage, setStatusMessage] = useState(null);

  // Handling Streaming Stop
  const eventSourceRef = useRef(null); // Add this line
  const pendingStreamingRaf = useRef(null); // rAF handle for streaming throttle
  const pendingReasoningRaf = useRef(null); // rAF handle for reasoning tool streaming throttle
  const pendingReasoningData = useRef(null); // holds latest reasoning data between frames
  const stopStreaming = async () => {
    if (eventSourceRef.current) {
      // eventSourceRef.current.close(); // Close the SSE connection
      // eventSourceRef.current = null;   // Clear the ref

      // eventSourceRef contains the streamId
      try {
        await stopStreamChat(eventSourceRef.current, userGoogleId);
        toast.success("Response generation stopped."); // Notify the user
        console.log("Streaming stopped by user.");
      } catch (err) {
        toast.error("Could not stop generation.");
      }

      // setSendTaskLoading(false); // Reset loading state to show the send button

      // Clear any partial response that was being streamed
      // setStreamingResponse({
      //   role: "assistant",
      //   content: [],
      //   tool_calls: [],
      //   reasoning: ""
      // });


      // setCurrentReasoningToolStreamingData(null); // Also clear any streaming reasoning data


    }
  };


  // Then the effect would run after the component re-renders.
  useEffect(() => {
    const newMap = new Map();
    currentToolCallStatus.forEach(tc => {
      newMap.set(tc.id, { status: tc.status, running_phase: tc.running_phase, approved: tc.approved, approvalNeeded: tc.approvalNeeded });
    });
    setCurrentToolCallStatusMap(newMap);

  }, [currentToolCallStatus]); // The effect depends on the list


  useEffect(() => {
    if (currentTaskId) {
      setConversations([]);
      setCreatingTask(false);
      setCurrentToolResults([]);
      setToolCallApprovalList([]);
      setCurrentToolCallStatus([]);
      setCurrentReasoningCalls([]);
      setCurrentReasoningToolStreamingData(null);
      setSelectedBotId(null);
      setCurrentStepId(null);
      setJustSavedFiles(true);
      setBotTaskId(false);
      setShowBotSteps(false);
      setJustStartedBot(false);
      setNewTriggerName("");
      setStreamingResponse({
        role: "assistant",
        content: [],
        tool_calls: [],
      });
      setCurrentStepId(null);
      setBotStepInputToSubmit({});
      setMoveToNextStep(false);
      setBotSessionJustCreatedId(null);
      setCurrentTaskSessions({});
      setCurrentBotSessionChats({});
      setCurrentToolResponseForReference({}); // New for task reference
      // Logic for getting current task data source files
      // loadConversations(currentTaskId);
      fetchCurrentTaskDataSourceFiles();



    } else {
      setCurrentToolCallStatus([]);
      setCurrentReasoningCalls([]);
      setCurrentReasoningToolStreamingData(null);
      setStreamingResponse({
        role: "assistant",
        content: [],
        tool_calls: [],
      });
    }
  }, [currentTaskId]);

  useEffect(() => {
    console.log("Current Tool Responses for reference:", currentToolResponseForReference)
  }, [currentToolResponseForReference])

  useEffect(() => {
    if (userGoogleId) {
      // Logic for getting all data source files for current user
      console.log("userGoogleId changed:", userGoogleId)
      fetchDataSourceFiles();
    }
  }, [userGoogleId]);

  const fetchDataSourceFiles = async () => {
    try {
      const response = await listFilesDataSource(userGoogleId);
      if (response.error) {
        setError(response.error);
      } else {
        setAllUserDataSourceFiles(response);
      }
    } catch (error) {
      toast.error("Error fetching files");
      console.error("Error fetching files:", error);
      setError("Error fetching files");
    }
  };

  const fetchCurrentTaskDataSourceFiles = async () => {
    try {
      const response = await getTaskDataSources(currentTaskId, userGoogleId);
      console.log("Current Data Sources Fetched:", response)
      if (response.error) {
        setError(response.error);
      } else {
        setCurrentTaskDataSourceFiles(response.data_source_ids || []);
      }
    } catch (error) {
      toast.error("Error fetching files");
      console.error("Error fetching files:", error);
      setError("Error fetching files");
    }
  };
  useFileStatusPolling(
    allUserDataSourceFiles,
    userGoogleId,
    setAllUserDataSourceFiles,
    setError
  );



  useEffect(() => {
    if (userGoogleId && justSavedFiles) {
      fetchFiles();
      setJustSavedFiles(false);
    }
  }, [userGoogleId, justSavedFiles]);

  const closeBotStepsModal = () => {
    setBotStepsModalOpen(false);
    setSelectedBotId(null);
  };

  const navigate = useNavigate();

  const fetchFiles = async () => {
    try {
      const response = await listFiles(userGoogleId, currentTaskId);
      if (response.error) {
        setError(response.error);
      } else {
        setFiles(response);
        setError(null);
      }
    } catch (error) {
      toast.error("Error fetching files");

      console.error("Error fetching files:", error);
      setError("Error fetching files");
    }
  };


  useEffect(() => {
    const storedUserGoogleId = localStorage.getItem("projectToken");
    const storedUserIdToken = localStorage.getItem("userToken");

    if (!storedUserGoogleId || !storedUserIdToken) {
      navigate("/login");
    } else {
      setUserGoogleId(storedUserGoogleId);
      setUserIdToken(storedUserIdToken)
      loadTasks(storedUserGoogleId);
      const getUserInfoFunc = async () => {
        try {
          console.log("UserToken For User Info:", storedUserGoogleId)
          const response = await getUserInfo(storedUserIdToken);
          if (response.error) {
            setError(response.error);
          }
          console.log("user info:", response);
          setUserInfo(response);
          setError(null);
        } catch (e) {
          toast.error("An error occured while fetching user info");

          console.log("User info error", e);
          setError("An error occured while fetching user info");
        }
      };
      getUserInfoFunc();
    }
  }, []);

  const loadTasks = async (userGoogleId) => {
    try {
      const tasks = await fetchTasks(userGoogleId);
      console.log(tasks);
      setTasks(tasks);
      if (tasks.length > 0) {
        // setCurrentTaskId(tasks[0]._id);
        // loadConversations(tasks[0]._id);
        // setTools(tasks[0].tools);
        // console.log("Tools", tools, tasks[0].tools);
      }
    } catch (error) {
      toast.error("An error occured while fetching tasks");
      setError("An error occurred while fetching tasks.");
    }
  };

  // BOTS
  useEffect(() => {
    if (needBotFetch && userGoogleId) {
      setNeedBotFetch(false);
      loadBots(userGoogleId);
      loadBotsHistory(userGoogleId)
      fetchWorkflows(userGoogleId)
      // setNeedBotFetch(false);
    }
  }, [needBotFetch, userGoogleId]);
  // useEffect(() => {
  //   if (submitClarificationQuestions && userGoogleId && botCreationId){
  //     submitClarificationAnswersAndCreateBotSteps();
  //     setSubmitClarificationQuestions(false);
  //   }
  // }, [submitClarificationQuestions])

  const loadBots = async (userGoogleId) => {
    try {
      const temp_bots = await fetchBots(userGoogleId);
      console.log("Bots: ", temp_bots);
      setBots(temp_bots);
      // setNeedBotFetch(false);
    } catch (error) {
      console.log("Error in bot fetch:", error);
      toast.error("An error occured while fetching tasks");
      setError("An error occurred while fetching tasks.");
    }
  };
  async function fetchWorkflows(userGoogleId) {
    try {
      const data = await getWorkflows(userGoogleId); // Update the endpoint as needed
      //   const data = await response.json();
      setWorkflows(data);

    } catch (error) {
      toast.error(typeof error === "object" ? error.message : JSON.stringify(error))
    }
  }


  const loadBotsHistory = async (userGoogleId) => {
    try {
      const temp_bots_history = await fetchBotsHistory(userGoogleId);
      console.log("Bots History: ", temp_bots_history);
      setBotsHistory(temp_bots_history);

    } catch (error) {
      console.log("Error in bot fetch:", error);
      toast.error("Error:", error);
      setError("An error occurred while fetching tasks.");
    }
  }

  // Function to create a task bot and handle clarification questions
  const createAndHandleClarificationQuestions = async (
    instruction,
    name,
    trigger,
    apps,
    dataSources
  ) => {
    console.log({
      instruction: instruction,
      name: name,
      trigger: trigger,
      apps: apps,
      dataSources: dataSources,
    });
    try {
      // Create the task bot
      // Extract file IDs to send to the API
      // const selectedFileIds = currentTaskDataSourceFiles.map(
      //   (file) => file.file_id
      // );
      const selectedFileIds = currentTaskDataSourceFiles

      const botResponse = await createTaskBot({
        task: instruction,
        name: name === "" ? "Untitled" : name,
        trigger,
        apps,
        bot_id: botCreationId,
        // data_sources: dataSources,
        selected_data_source_file_ids: selectedFileIds,
      }, userGoogleId);

      if (botResponse.error) {
        throw new Error(botResponse.error);
      }

      // Save the current bot ID and details
      setBotCreationId(botResponse.bot_id);
      // setClarificationQuestions(botResponse.questions)
      // setCurrentBot(botResponse.bot);
      console.log("Bot Response:", botResponse);

      // Check if there are any clarification questions
      if (botResponse.questions && botResponse.questions.length > 0) {
        // Save the clarification questions in the state
        // Transform the list of questions into a list of objects with question and answer keys
        const formattedQuestions = botResponse.questions.map((question) => ({
          id: question.id,
          question: question.question,
          answer: "",
        }));

        // Save the clarification questions in the state
        setClarificationQuestions(formattedQuestions);

        // setClarificationQuestions(botResponse.questions);
      } else {
        console.log("No clarification questions returned");
      }

      return botResponse;
    } catch (error) {
      console.error(
        "Error in creating task bot and handling clarification questions:",
        error
      );
      toast.error(
        "Error in creating task bot and handling clarification questions"
      );

      throw error;
    }
  };

  // Function to submit clarification answers and create bot steps
  const submitClarificationAnswersAndCreateBotSteps = async () => {
    try {
      if (!botCreationId || !clarificationQuestions || !userGoogleId) {
        setError("Can not complete the request");
        return;
      }
      setIsBusy(true);
      // Prepare the data to be sent for creating bot steps
      const stepsData = {
        bot_id: botCreationId,
        clarification_questions: clarificationQuestions,
        // Add any additional data needed for creating bot steps
      };

      // Submit the answers and create the bot steps
      const stepsResponse = await createBotSteps(stepsData, userGoogleId);
      console.log("Bot Steps", stepsResponse);

      // Optionally, you can handle the response further, such as updating the UI or state

      setIsBusy(false);
      return stepsResponse;
    } catch (error) {
      console.error(
        "Error in submitting clarification answers and creating bot steps:",
        error
      );
      toast.error(
        "Error in submitting clarification answers and creating bot steps"
      );

      setIsBusy(false);
      throw error;
    }
  };

  useEffect(() => {
    if (newTaskSubmitted) {
      handleStreamSend();
      setNewTaskSubmitted(false);
    }
  }, [newTaskSubmitted]);


  useEffect(() => {
    console.log("All task Conversations:", conversations);
    setToolCallApprovalList([]);

    const updateCurrentAttachmentTaskFiles = () => {
      const fileIdsInConversations = conversations.filter(chat => chat.role === 'user' && chat.attachments).flatMap(chat => chat.attachments.map(att => att.file_id));
      if (fileIdsInConversations.length > 0) {
        setFiles(prevFiles => prevFiles.filter(file => !fileIdsInConversations.includes(file.file_id)));
      }
    }
    const handleAutomaticToolUse = async () => {
      if (isAutomatic && !selectedBotId && conversations.length) {
        const lastChat = conversations[conversations.length - 1];
        if (lastChat && lastChat.tool_calls && lastChat.tool_calls.length > 0) {
          // const functionsNeedingApproval = ["get_current_datetime", "send_email_through_gmail"]; // List of functions requiring approval

          // Check if all tool calls that require approval are marked as approved
          const updatedToolCalls = lastChat.tool_calls.map(toolCall => {
            const approvalNeeded = functionsNeedingApproval.has(toolCall.function.name);
            // const approvalNeeded = !functionsNotNeedingApproval.has(toolCall.function.name);

            // If the tool call requires approval, we ensure the approvalNeeded and approved fields are set
            return {
              ...toolCall,
              approvalNeeded: approvalNeeded ? true : false,
              approved: approvalNeeded && !toolCall.approved ? false : true, // If approval is needed, it's false, else true
            };
          });

          // Check if all required tool calls are approved
          const allApproved = updatedToolCalls.every(
            (toolCall) => !(toolCall.approvalNeeded && !toolCall.approved)
          );

          // If not all are approved, update the toolCallApprovalList state
          if (!allApproved) {
            setToolCallApprovalList(updatedToolCalls); // Update the list with tool calls needing approval
            return;
          }

          let allHandled = true;
          let tool_responses = [];
          const toolCalls = lastChat.tool_calls;

          try {
            // Simultaneous API calls using Promise.all
            const responses = await Promise.all(
              toolCalls.map(async (toolCall) => {
                const data = await handleFunctionCall(toolCall);
                if (!data) {
                  throw new Error("Failed to handle tool call");
                }
                return { ...data, functionName: toolCall.function.name };
              })
            );

            tool_responses.push(...responses);
            allHandled = false; // Assume none were handled initially
          } catch (error) {
            console.error("Error handling tool calls:", error);
            return;
          }
          if (!allHandled) {
            const modified_tool_responses = tool_responses.map(tool_response => {
              return { ...tool_response, approved: true };
              // return functionResponsesRequiringApproval.includes(tool_response.functionName) ? {...tool_response, approved: false} : {...tool_response, approved: true};
            })
            setCurrentToolResults(modified_tool_responses);

            // setHasTaskResults(true); // Only set hasTaskResults to true when all tool_uses are handled
          }
        }
      }
    };

    handleAutomaticToolUse();
    updateCurrentAttachmentTaskFiles();
  }, [conversations, isAutomatic]);

  useEffect(() => {
    console.log("Current Tool Results:", currentToolResults)
    if (currentToolResults.length > 0 && currentToolResults.every(tool_response => tool_response.approved)) {
      setHasTaskResults(true);
    }
  }, [currentToolResults]);



  useEffect(() => {
    if (hasTaskResults && isAutomatic) {
      handleStreamSend();
      setHasTaskResults(false); // Reset the state
    }
  }, [hasTaskResults]);

  /**
 * Processes conversations to add a status to each tool call in the original
 * assistant messages and aggregates them for UI display.
 * @param {Array<Object>} conversations The list of conversation messages.
 * @returns {{
 * finalConversations: Array<Object>,
 * pendingToolCalls: Array<Object>,
 * pendingReasonCalls: Array<Object>
 * }}
 */
  const addToolCallUI = (conversations) => {
    // Create a deep copy to safely add properties to the original message objects.
    const updatableConversations = structuredClone(conversations);

    // A map to link a tool_call_id to its corresponding tool_call object reference.
    const toolCallMap = new Map();

    // First, pass through the copied conversations to update tool_call statuses in place.
    for (const msg of updatableConversations) {
      // If it's an assistant message, set the initial status and map the calls for lookup.
      if (msg.role === "assistant" && msg.tool_calls) {
        for (const tc of msg.tool_calls) {
          tc.status = "running"; // Set initial status
          toolCallMap.set(tc.id, tc); // Map the object reference for future updates
        }
      }
      // If it's a tool response, find the original call in the map and update its status.
      else if (msg.role === "tool") {
        const originalToolCall = toolCallMap.get(msg.tool_call_id);
        if (originalToolCall) {
          originalToolCall.status = msg.is_error ? "failed" : "success";
        }
      }
    }

    // Now, run the aggregation logic on the conversations that have updated statuses.
    const finalConversations = [];
    let pendingToolCalls = [];
    let pendingReasonCalls = [];
    let i = 0;

    while (i < updatableConversations.length) {
      const msg = updatableConversations[i];
      // Always push the (now modified) message.
      finalConversations.push(msg);

      if (msg.role === "user") {
        // Start a new segment after a user prompt.
        let aggregatedToolCalls = [];
        let reasonCalls = [];
        let j = i + 1;

        // Collect tool call data until the next user prompt.
        while (j < updatableConversations.length && updatableConversations[j].role !== "user") {
          const nextMsg = updatableConversations[j];

          if (nextMsg.role === "assistant" && nextMsg.tool_calls?.length > 0) {
            // The status is already on the tool_call object, so we just filter and add.
            aggregatedToolCalls.push(...nextMsg.tool_calls.filter(tc =>
              tc.function?.name !== "record_thought_process"
            ));

            nextMsg.tool_calls.forEach(tc => {
              if (tc.function?.name === "record_thought_process") {
                reasonCalls.push(tc);
              }
            });
          }
          j++;
        }

        // If we encountered another user message (i.e. segment complete), add the aggregated UI messages.
        if (j < updatableConversations.length && aggregatedToolCalls.length > 0) {
          if (reasonCalls.length) {
            finalConversations.push({
              role: "reason",
              tool_calls: reasonCalls
            });
          }
          finalConversations.push({
            role: "tool_calls_ui",
            tool_calls: aggregatedToolCalls,
          });
        } else {
          // Otherwise, these tool calls are still pending.
          pendingToolCalls = aggregatedToolCalls;
          pendingReasonCalls = reasonCalls;
        }
      }
      i++;
    }

    return { finalConversations, pendingToolCalls, pendingReasonCalls };
  };

  // Helper function to aggregate tool calls after each user message.
  // const addToolCallUI = (conversations) => {
  //   const finalConversations = [];
  //   let pendingToolCalls = [];
  //   let pendingReasonCalls = [];
  //   let i = 0;

  //   while (i < conversations.length) {
  //     const msg = conversations[i];
  //     // Always push the original message.
  //     finalConversations.push(msg);

  //     if (msg.role === "user") {
  //       // Start a new segment after a user prompt.
  //       let aggregatedToolCalls = [];
  //       let reasonCalls = [];
  //       let j = i + 1;

  //       // Collect tool call data until the next user prompt.
  //       while (j < conversations.length && conversations[j].role !== "user") {
  //         const nextMsg = conversations[j];
  //         // If an assistant message has tool_calls, accumulate them.
  //         if (nextMsg.role === "assistant" && nextMsg.tool_calls && nextMsg.tool_calls.length > 0) {
  //           aggregatedToolCalls.push(...nextMsg.tool_calls.filter(tc =>
  //             tc.function?.name !== "record_thought_process"
  //             ).map(tc => ({...tc, status: "running" }))); // Set initial status to pending

  //             nextMsg.tool_calls.forEach(tc => {
  //               if (tc.function?.name === "record_thought_process") {
  //                 reasonCalls.push(tc);
  //             }
  //           });
  //         }
  //         // For tool responses, update the status in the aggregated list.
  //         else if (nextMsg.role === "tool") {
  //           // aggregatedToolCalls = aggregatedToolCalls.map((tc) =>
  //           //   tc.id === nextMsg.tool_call_id
  //           //     ? { ...tc, status: nextMsg.is_error ? "failed" : "success" }
  //           //     : tc
  //           // );
  //               // Find the specific tool call object in the array.
  //           const toolToUpdate = aggregatedToolCalls.find(
  //               (tc) => tc.id === nextMsg.tool_call_id
  //           );

  //           // If the tool call is found, directly add or update its 'status' property.
  //           if (toolToUpdate) {
  //               toolToUpdate.status = nextMsg.is_error ? "failed" : "success";
  //           }
  //         }
  //         j++;
  //       }

  //       // If we encountered another user message (i.e. segment complete), add the aggregated tool_calls_ui immediately after the user message.
  //       if (j < conversations.length && aggregatedToolCalls.length > 0) {
  //         // If we found any reasoning calls, insert one "reason" message
  //         if (reasonCalls.length) {
  //           finalConversations.push({
  //             role: "reason",
  //             tool_calls: reasonCalls
  //           });
  //         }

  //         finalConversations.push({
  //           role: "tool_calls_ui",
  //           tool_calls: aggregatedToolCalls,
  //         });
  //       } else {
  //         // Otherwise, these tool calls are still pending.
  //         pendingToolCalls = aggregatedToolCalls;
  //         pendingReasonCalls = reasonCalls;
  //       }

  //     }
  //     i++;
  //   }

  //   return { finalConversations, pendingToolCalls, pendingReasonCalls };
  // };

  // useEffect(() => {
  //   scrollToBottom();
  // }, [currentReasoningToolStreamingData, currentToolCallStatus, conversations, currentReasoningCalls])

  const loadConversations = async (taskId) => {
    try {
      // setTrackExecution("step-2");
      setIsBusy(true);
      const { chats, streaming_status } = await fetchTaskConversations(taskId, userGoogleId);

      let conversations = chats;
      console.log("Loaded Conversations:", conversations, streaming_status);

      // setCurrentChat({ role: "user", content: [] });
      setCurrentToolResults([]);

      // Process the conversations to add the aggregated "tool_calls_ui" messages.
      const { finalConversations, pendingToolCalls, pendingReasonCalls } = addToolCallUI(conversations);

      // Set the final aggregated conversations while keeping the original messages intact.
      setConversations(finalConversations);

      if (streaming_status?.is_streaming) {
        console.log("Task is in a streaming state. Locking UI and awaiting completion.");
        setIsStreaming(true);
        setSendTaskLoading(true);
        listenForCompletion(streaming_status.stream_id);
      } else {
        setIsStreaming(false);
        setSendTaskLoading(false);
      }

      // Set pending tool calls (those without a following user prompt) in a separate state.
      setCurrentToolCallStatus(pendingToolCalls);
      setCurrentReasoningCalls(pendingReasonCalls);

      // --- CORRECTED LOGIC: Set the taskPlan state directly from finalConversations ---
      const lastMessageWithPlan = [...finalConversations]
        .reverse()
        .find(msg => msg.plan && msg.plan.goals);

      // Set the plan state based on the last message found
      setCurrentTaskPlan(lastMessageWithPlan ? lastMessageWithPlan.plan.goals : []);
      // --- END OF CORRECTED LOGIC ---

      finalConversations.forEach((msg) => {
        if (msg && msg.role === "tool" && typeof msg.content === "string") {
          extractAndSetToolResponsesForReferencing(msg, setCurrentToolResponseForReference);
        }
      });


      // setConversations(conversations);
      console.log("Conversations:", conversations);
      const newSubtasks = conversations.filter((chat) => chat.isSubtask); // Local variable to hold the new subtasks
      setSubtasks(newSubtasks);
      console.log("subtasks set:", newSubtasks);
    } catch (error) {
      console.error(error)
      toast.error("An error occurred while fetching conversations");
      setError("An error occurred while fetching conversations.");
    } finally {
      setIsBusy(false);
    }
  };

  // In DataProvider.js
  const listenForCompletion = (streamId) => {
    if (completionEventSourceRef.current) {
      completionEventSourceRef.current.close();
    }
    const eventSource = completeStreamChat(streamId, userGoogleId);
    completionEventSourceRef.current = eventSource;

    const handleFinalSignal = (event) => {
      try {
        const payload = JSON.parse(event.data);
        console.log(`Completion signal received with status: ${payload.status}`);

        if (payload.status === 'done') {
          toast.success("New message received!");
          const { final_assistant_message } = payload;

          // ---> NEW: Update conversations without a full refresh <---
          // We add the final, complete assistant message to the end of the conversation list.
          setConversations(prev => [...prev, final_assistant_message]);
          // Clear the temporary streaming response placeholder
          setStreamingResponse({ role: "assistant", content: [], tool_calls: [] });

        } else {
          toast.error(payload.message || "An error occurred while generating the response.");
          // Optionally, you could reload here to revert to the last saved state.
          // loadConversations(currentTaskId); 
        }
      } catch (error) {
        console.error("Error handling completion signal:", error);
        // Fallback to a full refresh if parsing fails
        // loadConversations(currentTaskId);
      } finally {
        // Clean up everything
        eventSource.close();
        completionEventSourceRef.current = null;
        setIsStreaming(false);
        setSendTaskLoading(false);
      }
    };

    eventSource.addEventListener('done', handleFinalSignal);
    eventSource.addEventListener('error', handleFinalSignal);

    eventSource.onerror = (err) => {
      console.error("Completion listener connection failed.", err);
      eventSource.close();
      completionEventSourceRef.current = null;
      // Fallback: If the connection just drops, a refresh is the safest way to get the final state.
      // loadConversations(currentTaskId);
    };
  };

  useEffect(() => {
    if (userInfo) {
      const available_models = userInfo.available_models || [];
      if (currentTaskModel && !available_models.includes(currentTaskModel)) {
        // If the current model is not available, you can handle it here
        setCurrentTaskModel(null);
      }
    }
  }, [userInfo]);

  const handleStreamSend = async (subtaskTools = null, explicitInput = null) => {

    // Use explicitInput if provided, otherwise fallback to the input state
    const currentInputText = explicitInput !== null ? explicitInput : input;

    if (!currentInputText.trim() && !currentToolResults.length) return;
    // if (!currentInputText.trim() && !currentChat.content.length) return;
    setSendTaskLoading(true);
    setIsStreaming(true);
    setTrackExecution("step-2");

    // ================= STATUS MESSAGE =================
    setStatusMessage("Processing...");
    let firstChunkReceived = false;
    const statusTimers = [];

    const rand = (min, max) =>
      Math.floor(Math.random() * (max - min + 1)) + min;

    const scheduleStatusUpdate = (delay, text) => {
      const t = setTimeout(() => {
        if (!firstChunkReceived) setStatusMessage(text);
      }, delay);
      statusTimers.push(t);
    };

    let cumulativeDelay = 0;
    cumulativeDelay += rand(1000, 2500);
    scheduleStatusUpdate(cumulativeDelay, "Preparing context...");
    cumulativeDelay += rand(3000, 6000);
    scheduleStatusUpdate(cumulativeDelay, "Planning...");

    const clearStatus = () => {
      statusTimers.forEach(clearTimeout);
      statusTimers.length = 0;
      firstChunkReceived = true;
      setStatusMessage(null);
    };

    // ================= MAIN LOGIC =================
    try {
      setSelectedBotId(null);
      setError("");

      let newChat = []; // Local variable to hold the new chat
      if (currentInputText.trim()) {
        console.log("current task images:", currentTaskImages);
        const currentAttachedTaskFiles = files.filter(file => file.is_attachment);

        setInput("");
        newChat = [{
          role: "user",
          content: [{ "type": "text", "text": currentInputText.trim() }],
          attachments: currentAttachedTaskFiles,
        }];

        if (currentTaskImages.length > 0) {
          const newContent = [...newChat[0].content];

          for (const image of currentTaskImages) {
            newContent.push({
              type: "image_url",
              image_url: { url: image.url },
              image_id: image.id,
            });
          }

          newChat[0].content = newContent;
        }
      }

        let updatedConversations = [...conversations];

        if (currentToolResults.length) {
          newChat = [...currentToolResults];
        } else {
          if (
            currentToolCallStatus.length > 0 ||
            currentReasoningCalls.length > 0
          ) {
            let lastUserIndex = conversations
              .map((msg, idx) => (msg.role === "user" ? idx : -1))
              .filter((idx) => idx !== -1)
              .pop();

            if (lastUserIndex !== undefined && lastUserIndex !== -1) {
              if (currentReasoningCalls.length > 0) {
                updatedConversations.splice(lastUserIndex + 1, 0, {
                  role: "reason",
                  tool_calls: currentReasoningCalls,
                });
                lastUserIndex++;
              }

              if (currentToolCallStatus.length > 0) {
                updatedConversations.splice(lastUserIndex + 1, 0, {
                  role: "tool_calls_ui",
                  tool_calls: currentToolCallStatus,
                });
              }
            } else {
              if (currentReasoningCalls.length > 0) {
                updatedConversations.push({
                  role: "reason",
                  tool_calls: currentReasoningCalls,
                });
              }

              if (currentToolCallStatus.length > 0) {
                updatedConversations.push({
                  role: "tool_calls_ui",
                  tool_calls: currentToolCallStatus,
                });
              }
            }

            setCurrentToolCallStatus([]);
            setCurrentReasoningCalls([]);
          }
        }

        updatedConversations = [...updatedConversations, ...newChat];

        setConversations(updatedConversations);
        setCurrentTaskImages([]);
        setCurrentToolResults([]);

        // ================= TASK UPDATE =================
        setTasks((prevTasks) => {
          const taskIndex = prevTasks.findIndex(
            (t) => t._id === currentTaskId
          );
          if (taskIndex === -1) return prevTasks;

          const updatedTask = {
            ...prevTasks[taskIndex],
            reasoning_effort: selectedReason,
            is_web_search: isWebSearch,
            is_reasoning: isReasoning,
            is_canvas: isCanvas,
            is_agent_mode: isAgentMode,
            agent_sequence: agentSequence,
            mcp_connections: taskMCPConnections,
            oqlous_tools: taskOqlousTools,
            manual_tool_selection: taskManualToolSelection,
            task_model: currentTaskModel,
          };

          return [
            updatedTask,
            ...prevTasks.filter((_, idx) => idx !== taskIndex),
          ];
        });

        // ================= API =================
        const timezone =
          Intl.DateTimeFormat().resolvedOptions().timeZone;

        const { stream_id: streamId } = await initializeStreamChat(
          {
            tools,
            chats: updatedConversations,
            task_id: currentTaskId,
            reasoning_effort: selectedReason,
            is_web_search: isWebSearch,
            is_canvas: isCanvas,
            is_agent_mode: isAgentMode,
            is_reasoning: isReasoning,
            mcp_connections: taskMCPConnections,
            oqlous_tools: taskOqlousTools,
            manual_tool_selection: taskManualToolSelection,
            task_model: currentTaskModel,
            timezone,
          },
          userGoogleId
        );

        const eventSource = streamChat(streamId, userGoogleId);
        eventSourceRef.current = streamId;

        let toolCallsByIndex = new Map();

        let currentStreamingResponseMessage = {
          role: "assistant",
          tool_calls: [],
          content: [],
          reasoning: "",
          is_reasoning: false,
        };

      let isConnectionClosed = false;

      // RAF THROTTLE HELPER
      // Mutate currentStreamingResponseMessage freely; this queues
      // exactly ONE setStreamingResponse call per animation frame.
      const scheduleStreamingUpdate = () => {
        if (pendingStreamingRaf.current !== null) return; // already queued
        pendingStreamingRaf.current = requestAnimationFrame(() => {
          pendingStreamingRaf.current = null;
          setStreamingResponse({ ...currentStreamingResponseMessage });
        });
      };

      const flushStreamingUpdate = () => {
        if (pendingStreamingRaf.current !== null) {
          cancelAnimationFrame(pendingStreamingRaf.current);
          pendingStreamingRaf.current = null;
        }
        setStreamingResponse({ ...currentStreamingResponseMessage });
      };

      // RAF THROTTLE HELPER — reasoning tool streaming data
      // Same pattern: buffer the latest data and flush once per frame.
      const scheduleReasoningUpdate = (data) => {
        pendingReasoningData.current = data;
        if (pendingReasoningRaf.current !== null) return; // already queued
        pendingReasoningRaf.current = requestAnimationFrame(() => {
          pendingReasoningRaf.current = null;
          if (pendingReasoningData.current !== null) {
            setCurrentReasoningToolStreamingData(pendingReasoningData.current);
          }
        });
      };

      const flushReasoningUpdate = () => {
        if (pendingReasoningRaf.current !== null) {
          cancelAnimationFrame(pendingReasoningRaf.current);
          pendingReasoningRaf.current = null;
        }
        if (pendingReasoningData.current !== null) {
          setCurrentReasoningToolStreamingData(pendingReasoningData.current);
          pendingReasoningData.current = null;
        }
      };

        // ================= STREAM =================
        eventSource.onmessage = async (event) => {
          if (!firstChunkReceived) {
            clearStatus();
            firstChunkReceived = true;
          }

          setTrackExecution(null);

          let chunk;
          try {
            chunk = JSON.parse(event.data);
          } catch {
            return;
          }

          if (!chunk?.choices) return;

          for (const choice of chunk.choices) {
            const content =
              choice.delta?.content ||
              choice.delta?.reasoning_content ||
              "";

            if (content) {
              currentStreamingResponseMessage.is_reasoning = false;

            if (!currentStreamingResponseMessage.content.length) {
              currentStreamingResponseMessage.content = [
                { type: "text", text: content },
              ];
            } else {
              currentStreamingResponseMessage.content[0].text += content;
            }

            scheduleStreamingUpdate();
          }

          if (choice.delta?.reasoning) {
            currentStreamingResponseMessage.is_reasoning = true;
            currentStreamingResponseMessage.reasoning += choice.delta.reasoning;

            scheduleStreamingUpdate();
          }

            if (choice.delta?.tool_calls) {
              choice.delta.tool_calls.forEach((toolCall) => {
                const index = toolCall.index;
                if (index === undefined) return;

                if (!toolCallsByIndex.has(index)) {
                  toolCallsByIndex.set(index, {
                    index,
                    id: toolCall.id || "",
                    function: { arguments: "", name: "" },
                    type: "function",
                  });
                }

                const existing = toolCallsByIndex.get(index);

                if (toolCall.function?.name)
                  existing.function.name = toolCall.function.name;

              if (toolCall.function?.arguments) {
                existing.function.arguments += toolCall.function.arguments;

                if (existing.function.name === "record_thought_process") {
                  scheduleReasoningUpdate({
                    id: existing.id,
                    type: "function",
                    status: "reasoning",
                    function: {
                      name: "record_thought_process",
                      arguments: existing.function.arguments,
                    },
                  });
                }
              }

                currentStreamingResponseMessage.tool_calls = Array.from(
                  toolCallsByIndex.values()
                )
                  .sort((a, b) => a.index - b.index)
                  .map((tc) => ({
                    id: tc.id,
                    function: tc.function,
                    type: "function",
                  }));

              scheduleStreamingUpdate();
            });
          }
        }
      };

        // ================= STREAM END =================
        eventSource.addEventListener("streamEnd", async (event) => {
          if (isConnectionClosed) return;
          isConnectionClosed = true;

          clearStatus();

          try {
            const data = JSON.parse(event.data);
            toolCallsByIndex = new Map(
              Object.entries(data.tool_calls_by_index || {})
            );

            currentStreamingResponseMessage.content =
              data.current_streaming_response?.content || null;

          currentStreamingResponseMessage.reasoning =
            data.current_streaming_response?.reasoning || null;
        } catch (err) {
          console.error(err);
        }

        // Flush any pending rAF update synchronously before closing
        flushStreamingUpdate();
        flushReasoningUpdate();

          eventSource.close();
          eventSourceRef.current = null;

          await handleStreamedResponse(
            newChat,
            currentStreamingResponseMessage,
            toolCallsByIndex,
            updatedConversations
          );
        });

        // ================= ERROR =================
        eventSource.onerror = (event) => {
          clearStatus();

          const err = JSON.parse(event?.data || "{}");

          eventSource.close();
          eventSourceRef.current = null;

          // Cancel any pending RAF updates
          if (pendingStreamingRaf.current !== null) {
            cancelAnimationFrame(pendingStreamingRaf.current);
            pendingStreamingRaf.current = null;
          }
          if (pendingReasoningRaf.current !== null) {
            cancelAnimationFrame(pendingReasoningRaf.current);
            pendingReasoningRaf.current = null;
          }
          pendingReasoningData.current = null;

          toast.error(
            typeof err === "string"
              ? err
              : err?.error || JSON.stringify(err)
          );

          setSendTaskLoading(false);
          setIsStreaming(false);
        };

        eventSource.onclose = () => {
          clearStatus();
          eventSourceRef.current = null;

          setSendTaskLoading(false);
          setIsStreaming(false);
        };
      } catch (err) {
        clearStatus();

        setError(JSON.stringify(err));
        toast.error(
          typeof err === "string" ? err : JSON.stringify(err)
        );

        setSendTaskLoading(false);
        setIsStreaming(false);
      }
    };

    const handleStreamedResponse = async (newChat, currentStreamingResponseMessage, toolCallsByIndex, updatedConversations) => {
      // for (const chat of newChat) {
      //   await addConversationToTask(currentTaskId, chat, userGoogleId);
      // }
      currentStreamingResponseMessage.is_reasoning = false;
      currentStreamingResponseMessage.tool_calls = Array.from(toolCallsByIndex.values())
        .sort((a, b) => a.index - b.index) // Sort by index for consistent ordering
        .map(toolCall => ({
          id: toolCall.id, // Assuming id might be present in some chunks, but index is primary
          function: {
            arguments: toolCall.function.arguments,
            name: toolCall.function.name,
          },
          status: "running", // Set status to running for UI consistency
          type: "function",
        }));
      // await addConversationToTask(
      //   currentTaskId,
      //   currentStreamingResponseMessage,
      //   userGoogleId
      // );
      setConversations([
        ...updatedConversations,
        currentStreamingResponseMessage,
      ]);
      setCurrentToolCallStatus(prevToolStatus => [...prevToolStatus, ...(currentStreamingResponseMessage?.tool_calls || []).map(tool_call => {
        return { ...tool_call, status: "running" }
      }).filter(tool_call => tool_call.function?.name !== "record_thought_process")]);
      setCurrentReasoningCalls(prevToolStatus => [...prevToolStatus, ...(currentStreamingResponseMessage?.tool_calls || []).filter(tool_call => tool_call.function?.name === "record_thought_process")]);
      setCurrentReasoningToolStreamingData(null);
      setStreamingResponse({
        role: "assistant",
        content: [],
        tool_calls: [],
        reasoning: ""
      });
      // scrollToBottom();
      setSendTaskLoading(false);
      setIsStreaming(false);
      setError(null);
      console.log(
        "Final Streaming Response:",
        currentStreamingResponseMessage
      );
    }

    const handleCreateTask = () => {
      setCreatingTask(true);
      setSteps("");
      setCurrentTaskId("");
      setCurrentTaskDataSourceFiles([]);
      setConversations([]);
      setSelectedBotId(null);
      setCurrentStepId(null);
      setJustSavedFiles(true);
      setBotTaskId(false);
      setShowBotSteps(false);
      setJustStartedBot(false);
      setNewTriggerName("");
      setCurrentStepId(null);
      setBotStepInputToSubmit({});
      setMoveToNextStep(false);
      setBotSessionJustCreatedId(null);
      setCurrentTaskSessions({});
      setCurrentBotSessionChats({});
      setCurrentToolResponseForReference({}); // new for tool response reference
    };

    const handleSubmitTask = (taskInputParam = null, explicitInput = null) => {

      // If explicitInput is provided, use it (from AutoResizableTextarea), 
      // otherwise fallback to taskInputParam, otherwise fallback to taskInput state
      const currentInputText = explicitInput !== null ? explicitInput :
        (typeof taskInputParam === 'string' ? taskInputParam : taskInput);

      console.log("Hanlde submit task called")
      if (currentInputText.trim()) {
        setTrackExecution("step-1");
        // scrollToBottom();
        setCreateTaskLoading(true);
        setIsBusy(true);
        createTask({
          task: currentInputText,
          reasoning_effort: selectedReason,
          is_web_search: isWebSearch,
          is_reasoning: isReasoning,
          is_canvas: isCanvas,
          is_agent_mode: isAgentMode,
          agent_sequence: agentSequence,
          mcp_connections: taskMCPConnections,
          oqlous_tools: taskOqlousTools,
          manual_tool_selection: taskManualToolSelection,
          task_model: currentTaskModel
        }, userGoogleId)
          .then(async (data) => {
            if (data.error) {
              setError(data.error);
            } else {
              const newTasks = [
                {
                  _id: data.task_id,
                  task_description: currentInputText,
                  tools: data.tools,
                },
                ...tasks,
              ]; // Local variable to hold the new tasks
              const selectedDataSourceFileIds = currentTaskDataSourceFiles
              // const selectedDataSourceFileIds = currentTaskDataSourceFiles.map(
              //   (file) => file.file_id
              // );
              const result = await updateTaskDataSources(
                data.task_id,
                userGoogleId,
                selectedDataSourceFileIds
              );
              if (result.error) {
                throw new Error(result.error);
              }
              setTasks(newTasks);



              setTools(data.tools);
              console.log("Task tools:", data.tools);
              // console.log("Task _id", data._id);
              console.log("task_id", data.task_id);
              // console.log("Tools:", data.tools);
              setConversations([]);

              setInput(currentInputText);
              setCurrentTaskId(data.task_id);
              setNewTaskSubmitted(true);

              setTaskInput("");
              setCreatingTask(false);
              setError(null);
              // scrollToBottom();
              setCreateTaskLoading(false);
            }
          })
          .catch((error) => {
            console.error(error);
            toast.error("An error occurred while creating the task");
            setError("An error occurred while creating the task.");
            setCreateTaskLoading(false);
          })
          .finally(() => {
            setCreateTaskLoading(false);
            setIsBusy(false);
          });
      }
    };


    const handleFunctionCall = async (tool) => {
      try {
        setSendTaskLoading(true);

        const finalArguments = prepareFunctionArguments(tool, currentToolResponseForReference);

        const data = await callFunction({
          id: tool.id,
          name: tool.function.name,
          // arguments: tool.function.arguments,
          arguments: finalArguments,
          type: tool.type,
          disapproved: tool.disapproved,
          disapprovalReason: tool.disapprovalReason,
          task_id: currentTaskId,
        }, userGoogleId);

        extractAndSetToolResponsesForReferencing(data, setCurrentToolResponseForReference);

        setCurrentToolCallStatus(prevToolStatus =>
          prevToolStatus.map(prevTool =>
            prevTool.id !== tool.id
              ? prevTool
              : (data.is_error || data.error)
                ? { ...prevTool, status: "failed" }
                : { ...prevTool, status: "success" }
          )
        );

        if (data.error) {
          console.log("Function calling error:", data.error);
          setError(data.error);
          return false;
        } else {
          if (
            tool.function.name === "download_and_save_attachments_on_gmail" || tool.function.name === "performCRUDOperationsOnUserFilesAndGenerateDataVisualizations" ||
            tool.function.name === "download_files_google_drive" || tool.function.name === "download_files_from_shared_urls_from_google_drive" || tool.function.name === "create_docx_document"
            || tool.function.name === "download_file_from_onedrive"
          ) {
            // setJustSavedFiles(false);
            setJustSavedFiles(true);
          }
          if (!data.is_error) {
            if (tool.function?.name?.startsWith("handoff_to") || tool.function?.name?.startsWith("return_to")) {
              if (data.agent_sequence) setAgentSequence(data.agent_sequence);
            }
          }


          setError(null);
          return data;
        }
      } catch (error) {
        console.error("ERRRRRRROR:", error);
        toast.error(`${error}`);
        setError("An error occurred while calling the function.");
        return false;
      } finally {
        setSendTaskLoading(false);
      }
    };

    const handleDeleteTask = async (taskId) => {
      setIsBusy(true);
      try {
        await deleteTask(taskId, userGoogleId); // Call your delete API function with token
        setTasks(tasks.filter((task) => task._id !== taskId)); // Update the tasks state to remove the deleted task
        if (currentTaskId === taskId && tasks.length > 1) {
          setCurrentTaskId(tasks[1]._id);
          loadConversations(tasks[1]._id);
        } else if (tasks.length === 1) {
          setCurrentTaskId(null);
          setConversations([]);
          setTools([]);
        }
        setError(null);
      } catch (error) {
        toast.error("Error deleting task");
        console.error("Error deleting task:", error);
        setError("An error occurred while deleting the task.");
      } finally {
        setIsBusy(false);
      }
    };

    const handleAutomaticChange = (e) => {
      setIsAutomatic(e.target.checked);
      console.log("isautomatic:", e.target.checked);
      if (e.target.checked) {
        // setShowTools(true);
      } else {
        setShowTools(true);
      }
    };

    const handleShowToolsChange = (e) => {
      if (!isAutomatic) {
        setShowTools(true);
      } else {
        setShowTools(e.target.checked);
      }
    };

    const renderChatContent = (content) => {
      switch (content.type) {
        case "text":
          let text = content.text;
          if (text.includes("TASK STATUS:")) {
            // Split the text into two parts
            const parts = text.split("TASK STATUS:");
            text = parts[0].trim();
            const current_steps = parts[1].trim();
          } else {
            // console.log("The string does not contain 'TASK STATUS:'.");
          }
          // return <p style={{ whiteSpace: "pre-wrap" }}>{text}</p>;

          return <TextFormat response={text} sources={[]} />;
        case "tool_use":
          return (
            <div>
              <TextFormat response={content.name} sources={[]} />
              {!isAutomatic && !content.handled && (
                <button onClick={() => handleFunctionCall(content)}>
                  Confirm
                </button>
              )}
              {content.handled && <p>Tool Request Handled</p>}
            </div>
          );
        case "tool_result":
          return (
            <div>
              {typeof content.content === "object" ? (
                <TextFormat
                  response={JSON.stringify(content.content, null, 2)}
                  sources={[]}
                />
              ) : (
                <TextFormat response={content.content} sources={[]} />
              )}
              {content.is_error && (
                <TextFormat response={content.content} sources={[]} />
              )}
            </div>
          );
        case "step_response":
          let text1 = content.text;
          // if (text1.includes("TASK STATUS:")) {
          //   // Split the text into two parts
          //   const parts = text.split("TASK STATUS:");
          //   text1 = parts[0].trim();
          //   const current_steps = parts[1].trim();
          // } else {
          //   console.log("The string does not contain 'TASK STATUS:'.");
          // }
          // return <p style={{ whiteSpace: "pre-wrap" }}>{text}</p>;
          return (
            <pre className="whitespace-pre-wrap break-words bg-gray-100 dark:bg-gray-700 p-4 rounded-md text-gray-900 dark:text-gray-100 overflow-x-auto">
              {text1}
            </pre>
          );
        case "session":
          let session_id = content.session_id;
          let bot_id = content.bot_id;
          // if (text1.includes("TASK STATUS:")) {
          //   // Split the text into two parts
          //   const parts = text.split("TASK STATUS:");
          //   text1 = parts[0].trim();
          //   const current_steps = parts[1].trim();
          // } else {
          //   console.log("The string does not contain 'TASK STATUS:'.");
          // }
          // return <p style={{ whiteSpace: "pre-wrap" }}>{text}</p>;
          return (
            <pre className="whitespace-pre-wrap break-words bg-gray-100 dark:bg-gray-700 p-4 rounded-md text-gray-900 dark:text-gray-100 overflow-x-auto">
              Session ID: {session_id} Bot ID: {bot_id}
            </pre>
          );
        // return <TextFormat response={text1} sources={[]} />;
        default:
          return <p>Unknown content type</p>;
      }
    };

    const [showUploadModal, setShowUploadModal] = useState(false);
    // const scrollToBottom = () => {
    //   if (messageContainerRef.current) {
    //     messageContainerRef.current.scrollTop =
    //       messageContainerRef.current.scrollHeight;
    //   }
    // };
    const scrollToBottom = () => {
      const container = messageContainerRef.current;
      if (!container) return;

      const isAtBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight <= 75;


      if (isAtBottom) {
        container.scrollTop = container.scrollHeight;
      }
    };

    const [files, setFiles] = useState([]);
    return (
      <DataContext.Provider
        value={{
          trackExecution,
          scrollToBottom,
          tasks,
          setTasks,
          showUploadModal,
          setShowUploadModal,
          currentTaskId,
          setCurrentTaskId,
          conversations,
          setConversations,
          input,
          setInput,
          isAutomatic,
          setIsAutomatic,
          taskInput,
          setTaskInput,
          setNewTaskSubmitted,
          setCreateTaskLoading,
          // currentChat,
          // setCurrentChat,
          creatingTask,
          setCreatingTask,
          error,
          setError,
          tools,
          setTools,
          userGoogleId,
          setUserGoogleId,
          subtasks,
          setSubtasks,
          showTools,
          setShowTools,
          hasTaskResults,
          setHasTaskResults,
          isBusy,
          setIsBusy,
          loadConversations,
          renderChatContent,
          handleShowToolsChange,
          handleAutomaticChange,
          handleSubmitTask,
          handleCreateTask,
          // handleSend,
          steps,
          setSteps,
          messageContainerRef,
          justSavedFiles,
          setJustSavedFiles,
          createTaskLoading,
          sendTaskLoading,
          sendSubTaskLoading,
          files,
          setFiles,
          isCreatingSubTask,
          setIsCreatingSubTask,
          handleDeleteTask,
          userInfo,
          streamingResponse,
          handleStreamSend,
          bots,
          setBots,
          botsHistory,
          setBotsHistory,
          currentBotSessions,
          setCurrentBotSessions,
          setCurrentBotSession,
          currentBotSession,
          setCurrentBotId,
          currentBotId,
          setCurrentBot,
          currentBot,
          clarificationQuestions,
          setClarificationQuestions,
          needBotFetch,
          setNeedBotFetch,
          createAndHandleClarificationQuestions,
          botCreationId,
          setBotCreationId,
          submitClarificationAnswersAndCreateBotSteps,
          setSubmitClarificationQuestions,
          submitClarificationQuestions,
          isBotStepsModalOpen,
          selectedBotId,
          closeBotStepsModal,
          botTaskId,
          setBotTaskId,
          showBotSteps,
          setShowBotSteps,
          justStartedBot,
          setJustStartedBot,
          botStepInputToSubmit,
          setBotStepInputToSubmit,
          useAutomaticStepSwitching,
          setUseAutomaticStepSwitching,
          moveToNextStep,
          setMoveToNextStep,
          theme,
          setTheme,
          botSessionJustCreatedId,
          setBotSessionJustCreatedId,
          currentTaskSessions,
          setCurrentTaskSessions,
          currentBotSessionChats,
          setCurrentBotSessionChats,
          newTriggerName,
          setNewTriggerName,
          fetchFiles,
          allUserDataSourceFiles,
          setAllUserDataSourceFiles,
          currentTaskDataSourceFiles,
          setCurrentTaskDataSourceFiles,
          fetchDataSourceFiles,
          feedbackModalOpen,
          setFeedbackModalOpen,
          toolCallApprovalList, setToolCallApprovalList,
          justChangedLocalStorageData, setJustChangedLocalStorageData,
          currentToolResults, setCurrentToolResults,
          currentTaskImages, setCurrentTaskImages,
          workflows, setWorkflows,
          currentToolCallStatus, setCurrentToolCallStatus,
          currentToolCallStatusMap,
          currentReasoningCalls, setCurrentReasoningCalls,
          setCurrentReasoningToolStreamingData, currentReasoningToolStreamingData,
          selectedReason, setSelectedReason,
          isWebSearch, setIsWebSearch,
          isReasoning, setIsReasoning,
          isCanvas, setIsCanvas,
          isAgentMode, setIsAgentMode,
          agentSequence, setAgentSequence,
          agentTraversalPaths, setAgentTraversalPaths,
          toggleSidebar,
          setToggleSidebar,
          currentToolResponseForReference,
          taskMCPConnections, setTaskMCPConnections,
          taskOqlousTools, setTaskOqlousTools,
          taskManualToolSelection, setTaskManualToolSelection,
          scrollToToolId, setScrollToToolId,
          rightSidebarWidth, setRightSidebarWidth,
          currentTaskPlan, setCurrentTaskPlan,
          currentTaskModel, setCurrentTaskModel,
          // openNotifications,
          // setOpenNotifications,
          // activeNotificationId,
          // setActiveNotificationId,
          // collapsedNotificationIds,
          // setCollapsedNotificationIds,
          // addOpenNotification,
          // removeCollapsedNotificationId,
          // createNewTaskForNotification,
          stopStreaming, // Add the new function here
          isStreaming, // Add the new state here
          statusMessage,
          setStatusMessage,
        }}
      >
        {children}
      </DataContext.Provider>
    );
  };



  export default DataProvider;